import { useState, useEffect } from 'react'
import Spinner from '../components/Spinner'
import SEO from '../components/SEO/SEO'
import Layout from '../layout/Layout'
import PriorAuthSteps from '../components/PreAuthTool/Steps'
import PriorAuthTable from '../components/PreAuthTool/Table'
import Dropbox from '../components/Uploader/Dropbox'
import { BASE_URL } from '../constants'
import { getRecaptchaV3Token } from '../utils/recaptcha'
import useRecaptchaLoader from '../hooks/useRecaptchaLoader'

const PreAuthRequestLookup = ({
  click,
  isShown,
  closeModal,
  successfulSubmission,
  failedSubmission,
}) => {
  const [questionIds, setQuestionIds] = useState(['emergency'])
  const [_, render] = useState(0)
  const [componentToRender, setComponentToRender] = useState(null)
  const isDocDefined = typeof document !== 'undefined'

  // ensure recaptcha v3 script loads on pages using recaptcha
  const { loaded: recaptchaLoaded, error: recaptchaError } = useRecaptchaLoader()

  if (isDocDefined)
    if (isShown) {
      document.body.style.overflow = 'hidden'
    } else {
      document.body.style.overflow = 'unset'
    }

  const handleAnswerChange = (question, answer) => {
    if (questionIds.at(-1) !== question) questionIds.pop()
    questionnaire[question].selection = answer
    render(prev => prev + 1)

    const nextQuestion = questionnaire[question].nextQuestion[answer]

    if (typeof nextQuestion === 'string') {
      setComponentToRender(null)
      if (!questionIds.includes(nextQuestion))
        setQuestionIds([...questionIds, nextQuestion])
    }
    if (typeof nextQuestion === 'object') {
      if (questionIds.length > 1) {
        const complimentNextQuestionKey = Object.keys(
          questionnaire[question].nextQuestion
        ).filter(key => key !== answer)[0]
        const complimentNextQuestionValue =
          questionnaire[question].nextQuestion[complimentNextQuestionKey]
        if (questionIds.includes(complimentNextQuestionValue)) {
          const nextQuestionIndex = questionIds.indexOf(
            complimentNextQuestionValue
          )
          const remainingQuestions = questionIds.slice(nextQuestionIndex)
          const nextQuestionIds = questionIds
            .filter(questionId => !remainingQuestions.includes(questionId))
            .filter(Boolean)

          remainingQuestions.forEach(questionId => {
            questionnaire[questionId].selection = null
          })
          setQuestionIds([...nextQuestionIds])
        }
      }

      setComponentToRender(nextQuestion)
    }
  }

  const renderQuestion = questionId => {
    const question = questionnaire[questionId]
    const description = question?.question
    const options = question?.enum
    const selection = question?.selection

    return (
      <tr key={questionId}>
        <td className="px-4 py-2">{description}</td>
        {options?.map(option => (
          <td className="text-center ">
            <input
              className="accent-orange"
              type="radio"
              name={questionId}
              value={option}
              checked={selection === option}
              onChange={() => handleAnswerChange(questionId, option)}
            />
          </td>
        ))}
      </tr>
    )
  }

  return (
    <>
      <SEO />
      <Layout>
        <div className="relative">
          <div className="absolute inset-x-0 bottom-0 bg-gray-100 h-1/2" />
          <div className="mx-auto">
            <div className="relative">
              <div className="absolute inset-0">
                <img
                  className="object-cover w-full h-full"
                  src="videocallAtDesk.png"
                  alt="Healthcare provider on a video call"
                />
              </div>
              <div className="relative max-w-screen-xl px-4 py-16 mx-auto md:py-16 lg:py-32">
                <h1 className="text-3xl tracking-tight md:text-5xl text-darkBlue">
                  Prior Auth Request and Lookup <br />{' '}
                  <span className="text-globeGreen">
                    Check if Prior Auth is required.
                  </span>
                </h1>
              </div>
            </div>
          </div>
        </div>

        <section id="preAuthTool">
          <div className="max-w-screen-xl px-4 pt-10 mx-auto sm:px-6 lg:px-8">
            <div className="p-2 bg-green-light md:p-16">
              <h3 className="pb-4 text-xl font-bold tracking-tight uppercase md:text-3xl text-green">
                Check if a prior authorization request is required
              </h3>
              <div className="bg-[#f9f1c6] text-[#6c4a00] text-left p-4 mb-4">
                <p>
                  <span className="font-bold">DISCLAIMER</span>: All attempts
                  are made to provide the most current information on the Prior
                  Auth Needed Tool. However, this does NOT guarantee payment.
                  Payment of claims is dependent on eligibility, covered
                  benefits, provider contracts, correct coding and billing
                  practices. For specific details, please refer to the provider
                  manual. If you are uncertain that prior authorization is
                  needed, please contact at 855-579-3879.
                </p>
              </div>
              <div className="relative overflow-x-auto border shadow-md border-green">
                <table className="w-full text-left text-gray-500 border-collapse md:text-md dark:text-gray-400">
                  <thead className="text-sm text-gray-700 uppercase bg-gray-50 dark:bg-gray-700 dark:text-gray-400 border-b-1 border-green">
                    <tr>
                      <th className="px-4 py-2">Types of Services</th>
                      <th className="text-center w-14">Yes</th>
                      <th className="text-center w-14">No</th>
                    </tr>
                  </thead>
                  <tbody>
                    {questionIds.map(questionId => {
                      if (typeof questionId === 'string') {
                        return renderQuestion(questionId)
                      }
                      return questionId
                    })}
                  </tbody>
                </table>
              </div>
              {componentToRender}
            </div>
          </div>
        </section>
        <PriorAuthSteps />
        <PriorAuthTable />
      </Layout>
    </>
  )
}

export default PreAuthRequestLookup

const CodeCheck = ({}) => {
  const [code, setCode] = useState('')
  const [codeAuth, setCodeAuth] = useState()
  const [error, setError] = useState(null)
  const [loading, setLoading] = useState(false)
  const [alert, setAlert] = useState({
    show: false,
    title: '',
    message: '',
    subMessage: '',
    showUpload: false,
  })

  const checkCode = async () => {
    setError(null)
    setCodeAuth(null)
    setLoading(false)
    setAlert({
      show: false,
      title: '',
      message: '',
      subMessage: '',
      showUpload: false,
    })
    setAlert({
      show: false,
    })
    setLoading(true)
    let token
    try {
      token = await getRecaptchaV3Token('check_if_auth_required')
    } catch (err) {
      setError(err)
      setLoading(false)
      return
    }

    return fetch(`${BASE_URL}/Marketing/CheckIfAuthRequired`, {
      method: 'POST',
      body: JSON.stringify({
        id: code.replace(/\s+/g, ''),
        captcha_token: { token, version: 'v3', provider: 1 },
      }),
      headers: {
        'content-type': 'application/json',
      },
    })
      .then(res => {
        if (!res.ok) {
          const error = new Error(res.statusText)
          throw error
        }
        return res.json()
      })
      .then(json => {
        return setCodeAuth(json)
      })
      .catch(err => {
        setError(err)
        if (err.json) {
          return err.json.then(json => {
            error.value.message = json.message
          })
        }
      })
      .then(() => {
        return setLoading(false)
      })
  }

  useEffect(() => {
    if (codeAuth) {
      if (codeAuth?.is_valid_procedureCode) {
        const isCodeNotCovered =
          codeAuth?.auth_required_note === 'Code is not a covered service'
        if (codeAuth.auth_required) {
          setAlert({
            show: true,
            title: codeAuth?.description
              ? `Procedure: ${codeAuth?.description} | Code: ${codeAuth?.procedure_code}`
              : null,
            message: codeAuth?.auth_required_flag,
            subMessage: codeAuth?.auth_required_note
              ? `${codeAuth?.auth_required_note}.`
              : undefined,
            showUpload: true,
          })
        } else {
          setAlert({
            type: isCodeNotCovered ? 'error' : 'info',
            show: true,
            title: codeAuth?.description
              ? `Procedure: ${codeAuth?.description} | Code: ${codeAuth?.procedure_code}`
              : null,
            message: codeAuth?.auth_required_flag || 'No prior authorization required.',
            subMessage: codeAuth?.auth_required_note
              ? `${codeAuth?.auth_required_note}.`
              : undefined,
            showUpload: false,
          })
        }
      } else {
        setAlert({
          show: true,
          message:
            'Code not found. Please try again or contact customer support at 1-855-579-3879.',
          showUpload: false,
        })
      }
    }
  }, [codeAuth])

  return (
    <>
      <div className="w-full h-[1px]" />{' '}
      <div className="w-full mt-8 ">
        <p className="pb-2 text-center text-grey md:text-left">
          What codes are associated with the request? Can&#96;t find a code?{' '}
          <a
            className="inline-block underline text-green decoration-solid"
            href="#preAuthTable"
          >
            Check our prior authorization table list.
          </a>
        </p>
        <div className="flex flex-row justify-between mt-2 border-1 border-green">
          <input
            className="w-full px-8 py-3 text-lg bg-transparent placeholder:text-lightGrey"
            type="text"
            max="15"
            value={code}
            onChange={e => {
              setCode(e.target.value)
            }}
            placeholder="Code"
          />
          {loading && <Spinner />}
          <button
            type="submit"
            className="px-2 text-lg font-medium text-white bg-green"
            onClick={() => checkCode()}
          >
            Check
          </button>
        </div>
      </div>
      <Alert
        show={alert.show}
        type={alert.type}
        title={alert.title}
        message={alert.message}
        subMessage={alert.subMessage}
        showUpload={alert.showUpload}
      />
    </>
  )
}

const Alert = ({ type, message, show, showUpload, subMessage, title }) => {
  const [uploadStatus, setUploadStatus] = useState('selectFiles')
  const [fileList, setFileList] = useState([])
  const [openOverlay, setOpenOverlay] = useState(false)

  useEffect(() => {
    return () => {
      setUploadStatus('selectFiles')
      setFileList([])
    }
  }, [])

  useEffect(() => {
    if (uploadStatus === 'success' || uploadStatus === 'error')
      setOpenOverlay(true)
  }, [uploadStatus])

  if (show) {
    return (
      <div className="flex flex-col">
        <div
          className={`${
            type === 'info'
              ? 'bg-[#e0f8ff] border border-blue'
              : 'bg-[#fffafa] border border-red'
          } mt-4 shadow-md flex max-lg:flex-col`}
          role="alert"
        >
          <div
            className={`${
              type === 'info' ? 'text-[#1c3869]' : 'text-[#d2322d]'
            } flex flex-col items-center flex-1 mx-4 py-4 text-lg text-left `}
          >
            {title && (
              <span
                style={{
                  fontSize: '16px',
                  fontWeight: 600,
                  marginBottom: '10px',
                }}
              >
                {title}
              </span>
            )}
            <p>
              {message}{' '}
              {showUpload && (
                <>
                  Click{' '}
                  <a
                    href="https://content.evryhealth.com/Files/Prior%20Authorization%20Request%20Form.pdf"
                    className="font-bold underline text-[#d2322d] animate-pulse"
                  >
                    here
                  </a>{' '}
                  to download the Prior Auth form. Once completed, please upload
                  the request form along with supporting documentation (e.g.
                  notes and lab or radiology findings) below.
                </>
              )}
            </p>
            {subMessage && (
              <p style={{ fontSize: '16px', color: 'gray', marginTop: '10px' }}>
                {subMessage}
              </p>
            )}
          </div>
        </div>
        {showUpload && (
          <div className="flex justify-center w-full h-full mt-5">
            <div className="relative flex flex-col max-w-[550px] h-full w-full">
              {openOverlay && (
                <StatusOverlay
                  status={uploadStatus}
                  fileList={fileList}
                  setOpenOverlay={setOpenOverlay}
                />
              )}
              <Dropbox
                uploadStatus={uploadStatus}
                setUploadStatus={setUploadStatus}
                setFileList={setFileList}
              />
              {uploadStatus === 'selectFiles' && (
                <div className="bg-[#dbe9fd] mt-2 p-4 border-[#006BFF] border-2 text-left">
                  <p>
                    Attach your Prior authorization request form with supporting
                    clinical documentation here. You can submit documents in{' '}
                    <span style={{ fontWeight: 600 }}>
                      {' '}
                      txt, doc, pdf, jpg and png{' '}
                    </span>{' '}
                    file formats. Please note that the maximum file size is{' '}
                    <span style={{ fontWeight: 600 }}>30mb</span>.
                  </p>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    )
  }

  return null
}

const StatusOverlay = ({ status, fileList, setOpenOverlay }) => {
  return (
    <div
      style={{ zIndex: 10 }}
      className="absolute inset-0 flex flex-col items-center justify-center flex-1 text-center bg-opacity-90 bg-white-light p-9 backdrop-blur-sm"
    >
      {status === 'success' ? (
        <>
          <span className="text-3xl">Successfully uploaded</span>
          <div className="flex flex-col items-center mt-4 p-9">
            <span className="text-xl">
              Here's a summary of the uploaded files
            </span>
            <div className="flex flex-col items-center w-full p-4 mt-4 overflow-hidden overflow-y-auto max-h-40">
              {fileList.map(file => (
                <span className="p-2 mb-2 rounded-md bg-[#dbf4db] drop-shadow-md border-[#5a8e5a] border-1">
                  {file}
                </span>
              ))}
            </div>
          </div>
        </>
      ) : (
        <>
          <span className="text-3xl">Failed to upload</span>
          <p className="text-xl text-center p-14">
            Unfortunately something went wrong uploading your files. Please
            contact customer service for further assistance
          </p>
        </>
      )}
      <button
        type="button"
        className={`w-[100px] p-2 px-4 mt-2 transition-all duration-200 border shadow-sm bg-[#007bff] border-blue hover:bg-[#1985fa] text-white-light disabled:bg-lightGrey disabled:text-black-default disabled:cursor-not-allowed`}
        onClick={() => setOpenOverlay(false)}
      >
        Close
      </button>
    </div>
  )
}

const questionnaire = {
  emergency: {
    enum: ['Yes', 'No'],
    selection: null,
    question: 'Is this an emergency?',
    nextQuestion: {
      Yes: (
        <Alert
          type="info"
          message="Please call 9-1-1. No prior authorization is required"
          showUpload={false}
          show
        />
      ),
      No: 'electiveCare',
    },
  },
  electiveCare: {
    enum: ['Yes', 'No'],
    selection: null,
    question:
      'Is this for elective care where an inpatient stay will be required?',
    nextQuestion: {
      Yes: <Alert show showUpload />,
      No: 'bariatricTransgenderFertilityCare',
    },
  },
  bariatricTransgenderFertilityCare: {
    enum: ['Yes', 'No'],
    selection: null,
    question:
      'Is this for care related to bariatric surgery, transgender care, fertility care, or care associated with child development delays?',
    nextQuestion: {
      Yes: (
        <Alert
          type="info"
          message="Benefit limitations may apply. Please call Member Services or your care guide to determine if there are covered services in your benefit plan."
          showUpload={false}
          show
        />
      ),
      No: 'outOfNetworkProvider',
    },
  },
  outOfNetworkProvider: {
    enum: ['Yes', 'No'],
    selection: null,
    question:
      'If you have EPO/EPBP benefits, is this for elective care provided by an out-of-network provider?',
    nextQuestion: {
      Yes: <Alert show showUpload />,
      No: 'infusionCancerBasedCare',
    },
  },
  infusionCancerBasedCare: {
    enum: ['Yes', 'No'],
    selection: null,
    question:
      'Is this for infusion-based or cancer-based care, including chemotherapy, radiation, biologic, drug therapy?',
    nextQuestion: {
      Yes: <Alert show showUpload />,
      No: 'retailClinicCare',
    },
  },
  retailClinicCare: {
    enum: ['Yes', 'No'],
    selection: null,
    question:
      'Is this for care to be received in a retail clinic, non-urgent setting?',
    nextQuestion: {
      Yes: (
        <Alert
          type="info"
          message="No prior authorization required."
          showUpload={false}
          show
        />
      ),
      No: 'chiropractorAcupuncturistCare',
    },
  },
  chiropractorAcupuncturistCare: {
    enum: ['Yes', 'No'],
    selection: null,
    question:
      'Is this for care to be delivered by either a chiropractor or an acupuncturist?',
    nextQuestion: {
      Yes: 'chiropractorAcupuncturistCare2',
      No: 'skilledNursingFacilityCare',
    },
  },
  chiropractorAcupuncturistCare2: {
    enum: ['Yes', 'No'],
    selection: null,
    question:
      'Benefit limitation may apply. The benefit plan design currently restricts members to 5 office visits before prior authorization is required. Is this your 6th or greater visit to a chiropractor or acupuncturist?',
    nextQuestion: {
      Yes: <Alert show showUpload />,
      No: 'skilledNursingFacilityCare',
    },
  },
  skilledNursingFacilityCare: {
    enum: ['Yes', 'No'],
    selection: null,
    question:
      'Is this for care to be delivered within a skilled nursing facility?',
    nextQuestion: {
      Yes: (
        <Alert
          type="info"
          message="No prior authorization required IF you have used less than 25 days of skilled nursing to date this calendar year.  If this care is expected to go beyond the 25 days limit, prior auth is required"
          showUpload={false}
          show
        />
      ),
      No: 'homeCaregiver',
    },
  },
  homeCaregiver: {
    enum: ['Yes', 'No'],
    selection: null,
    question:
      'Is this for care to be delivered by a home care aid or similar home-based caregiver?',
    nextQuestion: {
      Yes: (
        <Alert
          type="info"
          message="No prior authorization required if you have used less than 60 days of home-based care to date this calendar year. If this care is expected to go beyond the 60 days limit, prior auth is required"
          showUpload={false}
          show
        />
      ),
      No: 'rehabilitationTherapy',
    },
  },
  rehabilitationTherapy: {
    enum: ['Yes', 'No'],
    selection: null,
    question: 'Is this for physical therapy or occupational therapy?',
    nextQuestion: {
      Yes: (
        <Alert
          type="info"
          message="No prior authorization required if you have used less than 35 care visits of this type of rehabilitation to date this calendar year. If this care is expected to go beyond the 35 visits limitation, prior auth is required"
          showUpload={false}
          show
        />
      ),
      No: 'inquiryAnswered',
    },
  },
  inquiryAnswered: {
    enum: ['Yes', 'No'],
    selection: null,
    question: 'Has your inquiry been answered by the questions above?',
    nextQuestion: {
      Yes: (
        <Alert
          type="info"
          message="Thank you for using the Prior Auth Tool. Please contact us for further support if needed."
          showUpload={false}
          show
        />
      ),
      No: <CodeCheck />,
    },
  },
}
